export type Customer = {
  id: string;
  company: string;
  street: string;
  zip: string;
  city: string;
  country: string;
  poc: string;
};

export type UpsertCustomer = Omit<Customer, 'id'>;

import { isTestMode } from './dataService';
import api from './api';

function buildDummyCustomers(): Customer[] {
  const companies = ['Acme AG', 'Nordholz GmbH', 'TimberWorks KG', 'Wald & Co.', 'Holzmeister AG', 'Bayern Timber'];
  const cities = ['München', 'Berlin', 'Hamburg', 'Stuttgart', 'Köln', 'Leipzig'];
  return companies.map((company, i) => ({
    id: `cust-${i + 1}`,
    company,
    street: `Hauptstr. ${i + 10}`,
    zip: `80${(i + 10).toString().padStart(3, '0')}`,
    city: cities[i % cities.length],
    country: 'DE',
    poc: `Kontakt ${i + 1}`,
  }));
}

export async function listCustomers(params?: { q?: string }) {
  if (isTestMode()) {
    let items = buildDummyCustomers();
    const q = (params?.q || '').toLowerCase();
    if (q) items = items.filter(c => c.company.toLowerCase().includes(q) || c.city.toLowerCase().includes(q));
    return Promise.resolve({ items, total: items.length });
  }
  const res = await api.get('/customers', { params });
  return res.data as { items: Customer[]; total: number };
}

export async function listUserCustomers() {
  if (isTestMode()) {
    return Promise.resolve(buildDummyCustomers());
  }
  const res = await api.get('/user-assets/customers');
  return res.data as Customer[];
}

export async function getCustomer(id: string) {
  if (isTestMode()) {
    const item = buildDummyCustomers().find(c => c.id === id) || buildDummyCustomers()[0];
    return Promise.resolve(item);
  }
  const res = await api.get(`/customers/${id}`);
  return res.data as Customer;
}

export async function createCustomer(payload: UpsertCustomer) {
  if (isTestMode()) {
    const id = `cust-${Math.floor(Math.random() * 100000)}`;
    return Promise.resolve({ id, ...payload });
  }
  const res = await api.post('/customers', payload);
  return res.data as Customer;
}

export async function updateCustomer(id: string, payload: Partial<UpsertCustomer>) {
  if (isTestMode()) {
    const baseItem = buildDummyCustomers()[0];
    return Promise.resolve({ ...baseItem, id, ...payload } as Customer);
  }
  const res = await api.patch(`/customers/${id}`, payload);
  return res.data as Customer;
}

export async function deleteCustomer(id: string) {
  if (isTestMode()) {
    return Promise.resolve();
  }
  await api.delete(`/customers/${id}`);
}
